// ═══════════════════════════════════════════════════════════════
// History — cross-customer list of fully closed jobs (closedAt set
// AND status === 'paid'). The terminal archive: work is done, money
// is in. Sorted by closedAt desc so the most recent finish is at
// the top. Tap a row to expand full job details; use "View customer"
// to jump to the customer page if needed.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ClipboardCheck, Clock, Car, ChevronDown, ChevronUp,
  ExternalLink, DollarSign, Calendar, RotateCcw, ImageIcon, Ban, Undo2,
} from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import {
  formatPrice, daysSince, daysAgoLabel, toDate, isFullyClosed,
  isCancelled, isTerminal, cancelReasonLabel, jobsForActiveCustomers,
} from './lib/job-helpers.js';
import { formatDocumentNumber } from './lib/job-number.js';
import { uncancelJob } from './lib/db-crud.js';
import { vehicleLabel } from './Vehicles.jsx';
import PublicBreakdown from './PublicBreakdown.jsx';
import { formatInvoiceNumber } from './lib/job-number.js';

function formatLongDate(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatShortDateTime(d) {
  if (!d) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function paymentSourceLabel(closedBy) {
  if (closedBy === 'mark-paid') return 'Marked paid by you';
  if (closedBy === 'customer-paid') return 'Marked paid via invoice link';
  return 'Marked paid';
}

function hasLineItemBreakdown(job) {
  const items = Array.isArray(job?.lineItems) ? job.lineItems : [];
  const supplies = Number(job?.shopSupplies) || 0;
  return items.length > 0 || supplies > 0;
}

export default function History() {
  const { jobs: allJobs, customers, allCustomers, vehicles, ready } = useUserData();

  // Archived customers drop off this list with everything else of
  // theirs. Restore brings the whole record back untouched.
  const jobs = useMemo(() => jobsForActiveCustomers(allJobs, allCustomers), [allJobs, allCustomers]);
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');   // all | completed | cancelled

  const customerById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c])),
    [customers]
  );
  const vehicleById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.id, v])),
    [vehicles]
  );

  // History holds everything that's finished with, either way: paid
  // and closed, or cancelled. Sorted by whichever timestamp ended it.
  const allDone = useMemo(() => {
    return jobs
      .filter(isTerminal)
      .map((j) => {
        const cancelled = isCancelled(j);
        const endedAt = cancelled ? toDate(j.cancelledAt) : toDate(j.closedAt);
        return {
          ...j,
          _cancelled: cancelled,
          _endedAt: endedAt,
          _closedAt: toDate(j.closedAt),
          _paidAt: toDate(j.paidAt),
          _scheduledAt: toDate(j.scheduledAt),
        };
      })
      .sort((a, b) => (b._endedAt?.getTime() ?? 0) - (a._endedAt?.getTime() ?? 0));
  }, [jobs]);

  const completedJobs = useMemo(() => allDone.filter((j) => !j._cancelled), [allDone]);
  const cancelledJobs = useMemo(() => allDone.filter((j) => j._cancelled), [allDone]);

  const closedJobs = filter === 'completed' ? completedJobs
    : filter === 'cancelled' ? cancelledJobs
    : allDone;

  // Only completed work counts as earned — a cancelled job's price is
  // money that never arrived.
  const totalEarned = useMemo(
    () => completedJobs.reduce((sum, j) => sum + (Number(j.totalPrice) || 0), 0),
    [completedJobs]
  );

  async function handleUncancel(j) {
    try {
      await uncancelJob(jobsCol(), j);
      setExpandedId(null);
    } catch (e) {
      alert(`Could not restore: ${e.message}`);
    }
  }

  function toggleExpanded(id) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  async function handleReopen(j) {
    if (!confirm(`Reopen "${j.description}"?\n\nThis moves it back to Outstanding as an unpaid invoice.`)) return;
    try {
      await updateOne(jobsCol(), j.id, {
        status: 'invoiced',
        closedAt: null,
        closedBy: null,
        paidAt: null,
      });
      setExpandedId(null);
    } catch (e) {
      alert(`Could not reopen: ${e.message}`);
    }
  }

  return (
    <div>
      <div className="screen-header">
        <div>
          <Link to="/" className="btn-link" style={{ marginBottom: 4 }}>
            <ArrowLeft size={14} /> Home
          </Link>
          <h1>History</h1>
          <p className="subtitle">
            {ready
              ? (allDone.length === 0
                  ? 'Nothing finished yet.'
                  : `${completedJobs.length} closed · ${formatPrice(totalEarned)} earned`
                    + (cancelledJobs.length > 0 ? ` · ${cancelledJobs.length} cancelled` : ''))
              : 'Loading…'}
          </p>
        </div>
      </div>

      {ready && cancelledJobs.length > 0 && (
        <div className="history-filter-row" role="tablist" aria-label="Filter history">
          {[
            { key: 'all',       label: `All (${allDone.length})` },
            { key: 'completed', label: `Completed (${completedJobs.length})` },
            { key: 'cancelled', label: `Cancelled (${cancelledJobs.length})` },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={filter === t.key}
              className={`history-filter-chip${filter === t.key ? ' active' : ''}`}
              onClick={() => setFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!ready && <p className="muted small">Loading history…</p>}

      {ready && allDone.length === 0 && (
        <div className="empty-state">
          <ClipboardCheck size={32} color="var(--text-3)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <p style={{ fontWeight: 600 }}>Nothing here yet.</p>
          <p className="muted small">
            Jobs land here once they're closed out and marked paid.
          </p>
        </div>
      )}

      {ready && closedJobs.length > 0 && (
        <ul className="history-list">
          {closedJobs.map((j) => {
            const customer = customerById[j.customerId];
            const vehicle  = vehicleById[j.vehicleId];
            const days     = daysSince(j._cancelled ? j._endedAt : j._closedAt);
            const expanded = expandedId === j.id;
            const hasNotes = !!(j.workSummary || j.extraParts || j.returnNotes);
            const hasBreakdown = hasLineItemBreakdown(j);

            return (
              <li
                key={j.id}
                className={`history-row ${expanded ? 'expanded' : ''}${j._cancelled ? ' cancelled' : ''}`}
              >
                <div className="history-row-summary">
                  <button
                    type="button"
                    className="history-expand-btn"
                    onClick={() => toggleExpanded(j.id)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse job details' : 'Expand job details'}
                  >
                    {expanded
                      ? <ChevronUp size={18} />
                      : <ChevronDown size={18} />}
                  </button>
                  <button
                    type="button"
                    className="history-row-body"
                    onClick={() => toggleExpanded(j.id)}
                  >
                    <div className="history-row-head">
                      <span className="history-customer">
                        {customer?.name || '(deleted customer)'}
                        {/* Cancelled jobs keep their number — that's the
                            point of cancelling rather than deleting. A
                            cancelled ticket still reads EST-####; only a
                            paid one graduates to INV-####. */}
                        {formatDocumentNumber(j) && (
                          <span className="doc-number mono">{formatDocumentNumber(j)}</span>
                        )}
                      </span>
                      <span className={`history-amount mono${j._cancelled ? ' void' : ''}`}>
                        {formatPrice(j.totalPrice)}
                      </span>
                    </div>
                    <div className="history-row-desc">{j.description}</div>
                    <div className="history-row-foot">
                      {j._cancelled ? (
                        <span className="age-pill cancelled-pill">
                          <Ban size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                          {cancelReasonLabel(j.cancelReason)}
                          {j._endedAt && <> · {formatLongDate(j._endedAt)}</>}
                        </span>
                      ) : (
                        <span className="age-pill">
                          <ClipboardCheck size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                          Closed {formatLongDate(j._closedAt)}
                          {days != null && days > 0 && <> · {daysAgoLabel(days, '')?.replace(/^ /, '')}</>}
                        </span>
                      )}
                      {j.workMinutes != null && j.workMinutes > 0 && (
                        <span className="age-pill">
                          <Clock size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                          {Math.round(j.workMinutes)} min on site
                        </span>
                      )}
                      {vehicle && (
                        <span className="muted small">
                          <Car size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                          {vehicleLabel(vehicle)}
                        </span>
                      )}
                      {!expanded && (hasNotes || hasBreakdown || (Array.isArray(j.photos) && j.photos.length > 0)) && (
                        <span className="history-has-details muted small">Tap for details</span>
                      )}
                    </div>
                  </button>
                </div>

                {expanded && (
                  <div className="history-row-details">
                    {j._cancelled && (
                      <div className="history-detail-block">
                        <p className="history-detail-label">
                          <Ban size={12} /> Why it was cancelled
                        </p>
                        <p className="history-detail-value">
                          {cancelReasonLabel(j.cancelReason)}
                          {j.cancelledBy === 'customer' && (
                            <span className="muted small"> · declined by the customer</span>
                          )}
                        </p>
                        {j.cancelNote && (
                          <p className="history-detail-value">{j.cancelNote}</p>
                        )}
                      </div>
                    )}
                    {j._scheduledAt && (
                      <div className="history-detail-block">
                        <p className="history-detail-label">
                          <Calendar size={12} /> Service date
                        </p>
                        <p className="history-detail-value">{formatShortDateTime(j._scheduledAt)}</p>
                      </div>
                    )}

                    {hasBreakdown && (
                      <div className="history-detail-block">
                        <p className="history-detail-label">Estimate / invoice breakdown</p>
                        <PublicBreakdown share={j} />
                      </div>
                    )}

                    <div className="history-detail-block">
                      <p className="history-detail-label">What you did</p>
                      <p className="history-detail-value">
                        {j.workSummary?.trim() || <span className="muted">No close-out notes recorded.</span>}
                      </p>
                    </div>

                    {(j.extraParts?.trim() || j.returnNotes?.trim()) && (
                      <div className="history-detail-block">
                        {j.extraParts?.trim() && (
                          <>
                            <p className="history-detail-label">Extra parts</p>
                            <p className="history-detail-value">{j.extraParts}</p>
                          </>
                        )}
                        {j.returnNotes?.trim() && (
                          <>
                            <p className="history-detail-label" style={{ marginTop: j.extraParts?.trim() ? 10 : 0 }}>
                              Return visit notes
                            </p>
                            <p className="history-detail-value">{j.returnNotes}</p>
                          </>
                        )}
                      </div>
                    )}

                    {!j._cancelled && (
                    <div className="history-detail-block history-payment-block">
                      <p className="history-detail-label">
                        <DollarSign size={12} /> Payment
                      </p>
                      <p className="history-detail-value">
                        {formatPrice(j.totalPrice)} · {paymentSourceLabel(j.closedBy)}
                        {j._paidAt && (
                          <span className="muted small"> · {formatShortDateTime(j._paidAt)}</span>
                        )}
                      </p>
                    </div>
                    )}

                    {(j.odometerIn != null || j.odometerOut != null) && (
                      <div className="history-detail-block">
                        <p className="history-detail-label">Mileage</p>
                        <p className="history-detail-value">
                          {j.odometerIn != null && <>In: {Number(j.odometerIn).toLocaleString()} mi</>}
                          {j.odometerIn != null && j.odometerOut != null && ' · '}
                          {j.odometerOut != null && <>Out: {Number(j.odometerOut).toLocaleString()} mi</>}
                          {j.odometerIn != null && j.odometerOut != null && Number(j.odometerOut) >= Number(j.odometerIn) && (
                            <span className="muted small">
                              {' '}(trip {Number(j.odometerOut) - Number(j.odometerIn)} mi)
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    {Array.isArray(j.photos) && j.photos.length > 0 && (
                      <div className="history-detail-block">
                        <p className="history-detail-label">
                          <ImageIcon size={12} /> Photos ({j.photos.length})
                        </p>
                        <ul className="job-photo-list history-photo-list">
                          {j.photos.map((p) => (
                            <li key={p.id || p.url} className="job-photo-card history-photo-card">
                              <a
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="job-photo-card-thumb-link"
                              >
                                <img src={p.url} alt="" className="job-photo-card-thumb" />
                              </a>
                              <div className="job-photo-card-body">
                                {p.note ? (
                                  <p className="history-detail-value">{p.note}</p>
                                ) : (
                                  <p className="muted small">No note</p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="history-detail-actions">
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => navigate(`/customers/${j.customerId}`)}
                      >
                        <ExternalLink size={13} /> View customer
                      </button>
                      {j._cancelled ? (
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => handleUncancel(j)}
                          title="Put this job back where it was before it was cancelled"
                        >
                          <Undo2 size={13} /> Restore job
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => handleReopen(j)}
                          title="Move back to Outstanding if marked paid by mistake"
                        >
                          <RotateCcw size={13} /> Reopen
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
